// Enhanced RTK Query Cache Tag Management System
// Provides centralized tag management with powerful invalidation patterns

// ============================================================================
// CORE TAG DEFINITIONS
// ============================================================================

export const API_TAGS = {
  // Core entities
  USER: "User",

  // Business entities
  PRACTICE: "Practice",
  PROFESSIONAL: "Professional",
  APPOINTMENT: "Appointment",
  PAYMENT: "Payment",
  REVIEW: "Review",
  SHIFT: "Shift",
  STATUSES_COUNT: "StatusesCount",
  LOOKUPS: "Lookups",
  TEMPLATES: "Templates",
  BRANCHES: "Branches",
  DOCUMENTS: "Documents",
  CERTIFICATES: "Certificates",
  AVAILABILITY: "Availability",
  NOTIFICATION: "Notification",

  // Aggregate rollups. REPORT is the dashboard/statistics invalidation tag, not a
  // reports feature — there is none. Admin, practice and professional stats queries
  // provide it, and mutations that move those numbers invalidate it.
  REPORT: "Report",
  CATALOGUE: "Catalogue",
  PRICING_PLAN: "PricingPlan",
  CURRENCY: "Currency",
  TIER: "Tier",

  // Special collections
  LIST: "LIST",
  SCHEDULE: "SCHEDULE",
  FLAGGED: "FLAGGED",
  PENDING: "PENDING",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",

  // Cache control
  CACHE_CONTROL: "CacheControl"
} as const;

export type ApiTag = (typeof API_TAGS)[keyof typeof API_TAGS];

// ============================================================================
// TAG FACTORY FUNCTIONS
// ============================================================================

export const createTag = (type: ApiTag, id?: string) => {
  return id ? { type, id } : { type };
};

export const createListTag = (type: ApiTag) => createTag(type, API_TAGS.LIST);

export const createScheduleTag = (type: ApiTag) =>
  createTag(type, API_TAGS.SCHEDULE);

export const createFlaggedTag = (type: ApiTag) =>
  createTag(type, API_TAGS.FLAGGED);

export const createPendingTag = (type: ApiTag) =>
  createTag(type, API_TAGS.PENDING);

export const createCompletedTag = (type: ApiTag) =>
  createTag(type, API_TAGS.COMPLETED);

export const createCancelledTag = (type: ApiTag) =>
  createTag(type, API_TAGS.CANCELLED);

// ============================================================================
// ADVANCED TAG BUILDER
// ============================================================================

export const TAG_BUILDER = {
  // Core-related tags
  USER: {
    ENTITY: (id: string) => createTag(API_TAGS.USER, id),
    LIST: () => createListTag(API_TAGS.USER),
    DOCUMENTS: (userId: string) => [
      createTag(API_TAGS.DOCUMENTS, API_TAGS.LIST),
      createTag(API_TAGS.USER, userId)
    ]
  },

  // Practice-related tags
  PRACTICE: {
    ENTITY: (id: string) => createTag(API_TAGS.PRACTICE, id),
    LIST: () => createListTag(API_TAGS.PRACTICE),
    PROFESSIONALS: (practiceId: string) => [
      createTag(API_TAGS.PROFESSIONAL, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    APPOINTMENTS: (practiceId: string) => [
      createTag(API_TAGS.APPOINTMENT, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    PAYMENTS: (practiceId: string) => [
      createTag(API_TAGS.PAYMENT, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    BRANCHES: (practiceId: string) => [
      createTag(API_TAGS.BRANCHES, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    DOCUMENTS: (practiceId: string) => [
      createTag(API_TAGS.DOCUMENTS, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    SHIFT_LOOKUPS: (practiceId: string) => [
      createTag(API_TAGS.LOOKUPS, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    SHIFT_TEMPLATES: (practiceId: string) => [
      createTag(API_TAGS.TEMPLATES, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ],
    REVIEWS: (practiceId: string) => [
      createTag(API_TAGS.REVIEW, API_TAGS.LIST),
      createTag(API_TAGS.PRACTICE, practiceId)
    ]
  },

  // Professional-related tags
  PROFESSIONAL: {
    ENTITY: (id: string) => createTag(API_TAGS.PROFESSIONAL, id),
    LIST: () => createListTag(API_TAGS.PROFESSIONAL),
    REVIEWS: (professionalId: string) => [
      createTag(API_TAGS.REVIEW, API_TAGS.LIST),
      createTag(API_TAGS.PROFESSIONAL, professionalId)
    ],
    SHIFTS: (professionalId: string) => [
      createTag(API_TAGS.SHIFT, API_TAGS.LIST),
      createTag(API_TAGS.PROFESSIONAL, professionalId)
    ],
    APPOINTMENTS: (professionalId: string) => [
      createTag(API_TAGS.APPOINTMENT, API_TAGS.LIST),
      createTag(API_TAGS.PROFESSIONAL, professionalId)
    ],
    SCHEDULE: (professionalId: string) => [
      createScheduleTag(API_TAGS.SHIFT),
      createTag(API_TAGS.PROFESSIONAL, professionalId)
    ],
    AVAILABILITY: (professionalId: string) => [
      createTag(API_TAGS.AVAILABILITY, professionalId)
    ],
    LOOKUPS: () => createTag(API_TAGS.PROFESSIONAL, API_TAGS.LOOKUPS),
    DOCUMENTS: (professionalId: string) => [
      createTag(API_TAGS.DOCUMENTS, API_TAGS.LIST),
      createTag(API_TAGS.PROFESSIONAL, professionalId)
    ],
    CERTIFICATES: (professionalId: string) => [
      createTag(API_TAGS.CERTIFICATES, API_TAGS.LIST),
      createTag(API_TAGS.PROFESSIONAL, professionalId)
    ]
  },

  NOTIFICATION: {
    FOR_ENTITY: (entityId: string) => createTag(API_TAGS.NOTIFICATION, entityId)
  },

  // Payment-related tags
  PAYMENT: {
    ENTITY: (id: string) => createTag(API_TAGS.PAYMENT, id),
    LIST: () => createListTag(API_TAGS.PAYMENT),
    PENDING: () => createPendingTag(API_TAGS.PAYMENT),
    COMPLETED: () => createCompletedTag(API_TAGS.PAYMENT),
    CANCELLED: () => createCancelledTag(API_TAGS.PAYMENT)
  },

  // Review-related tags
  REVIEW: {
    ENTITY: (id: string) => createTag(API_TAGS.REVIEW, id),
    LIST: () => createListTag(API_TAGS.REVIEW),
    FLAGGED: () => createFlaggedTag(API_TAGS.REVIEW),
    PENDING: () => createPendingTag(API_TAGS.REVIEW),
    APPROVED: () => createCompletedTag(API_TAGS.REVIEW)
  },

  // Shift-related tags
  SHIFT: {
    ENTITY: (id: string) => createTag(API_TAGS.SHIFT, id),
    LIST: () => createListTag(API_TAGS.SHIFT),
    SCHEDULE: () => createScheduleTag(API_TAGS.SHIFT),
    PENDING: () => createPendingTag(API_TAGS.SHIFT),
    COMPLETED: () => createCompletedTag(API_TAGS.SHIFT),
    CANCELLED: () => createCancelledTag(API_TAGS.SHIFT),
    STATUSES_COUNT: () => createTag(API_TAGS.SHIFT, API_TAGS.STATUSES_COUNT)
  },

  // Appointment-related tags
  APPOINTMENT: {
    ENTITY: (id: string) => createTag(API_TAGS.APPOINTMENT, id),
    LIST: () => createListTag(API_TAGS.APPOINTMENT),
    PENDING: () => createPendingTag(API_TAGS.APPOINTMENT),
    COMPLETED: () => createCompletedTag(API_TAGS.APPOINTMENT),
    CANCELLED: () => createCancelledTag(API_TAGS.APPOINTMENT)
  },

  // Admin-related tags
  ADMIN: {
    USERS: () => createListTag(API_TAGS.USER),
    /** Dashboard/statistics rollups. Not a reports feature — that surface was removed. */
    REPORTS: () => createTag(API_TAGS.REPORT),
    CATALOGUE: () => createTag(API_TAGS.CATALOGUE),
    PRICING_PLANS: () => createTag(API_TAGS.PRICING_PLAN),
    CURRENCIES: () => createTag(API_TAGS.CURRENCY),
    TIERS: () => createTag(API_TAGS.TIER)
  },

  // Cache control
  CACHE: {
    CONTROL: () => createTag(API_TAGS.CACHE_CONTROL),
    CLEAR_ALL: () => Object.values(API_TAGS).map((tag) => createTag(tag))
  }
} as const;

// ============================================================================
// INVALIDATION HELPERS
// ============================================================================

// Every generic invalidation helper that lived here had no callers, and the
// cache-strategy dispatcher that was their only consumer had none either. Endpoints name
// the tags they invalidate directly, through TAG_BUILDER, which is legible at the call
// site; a helper that fans out to per-status tags no query provides invalidates nothing
// while reading as though it invalidates a lot.

// ============================================================================
// TAG VALIDATION
// ============================================================================

export type Tag = { type: ApiTag; id?: string };

export const isValidTag = (tag: Tag) => {
  return (
    tag &&
    typeof tag === "object" &&
    typeof tag.type === "string" &&
    Object.values(API_TAGS).includes(tag.type as ApiTag) &&
    (tag.id === undefined || typeof tag.id === "string")
  );
};

export const validateTags = (tags: Tag[]): Tag[] => {
  return tags.filter(isValidTag);
};

// ============================================================================
// TAG UTILITIES
// ============================================================================

export const tagToString = (tag: { type: ApiTag; id?: string }) => {
  return tag.id ? `${tag.type}:${tag.id}` : tag.type;
};

export const stringToTag = (
  tagString: string
): { type: ApiTag; id?: string } | null => {
  const parts = tagString.split(":");
  if (parts.length === 1) {
    const type = parts[0] as ApiTag;
    return Object.values(API_TAGS).includes(type) ? { type } : null;
  }
  if (parts.length === 2) {
    const [type, id] = parts;
    return Object.values(API_TAGS).includes(type as ApiTag)
      ? { type: type as ApiTag, id }
      : null;
  }
  return null;
};

// ============================================================================
// API ENDPOINT ROUTES
// ============================================================================
// Paths are relative to `config.api.baseUrl` (typically includes `api/v{VERSION}` — see Tempii `ApiConstants.API_ROOT_PATH`).
// Backend route groups: `Tempii.Api/Endpoints/{Users,Practices,Professionals,Payments,Shifts,Admin}/…`.

export const API_ENDPOINTS = {
  PROFESSIONALS: {
    STATS: (id: string) => `/professionals/${id}/stats`,
    GET: (id: string) => `/professionals/${id}`,
    UPDATE: (id: string) => `/professionals/${id}/update-profile`,
    AVAILABILITY: (id: string) => `/professionals/${id}/availability`,
    AVAILABILITIES: (id: string) => `/professionals/${id}/availabilities`,
    AVAILABILITY_ENTRY: (id: string, availabilityId: string, dayOfWeek: string) =>
      `/professionals/${id}/availabilities/${availabilityId}/${dayOfWeek}`,
    /** Staff verification decision on a professional's credentials. */
    VERIFY: (id: string) => `/professionals/${id}/verify`,
    REGISTER: "/professionals/register-professional",
    LOOKUPS: "/professionals/lookups",
    DOCUMENTS: (id: string) => `/professionals/${id}/documents`,
    UPLOAD_DOCUMENT: (id: string) => `/professionals/${id}/upload-document`,
    DOCUMENT: (id: string, documentId: string) =>
      `/professionals/${id}/documents/${documentId}`,
    DOWNLOAD_DOCUMENT: (id: string, documentId: string) =>
      `/professionals/${id}/documents/${documentId}/download`,
    CERTIFICATES: (id: string) => `/professionals/${id}/certificates`,
    CERTIFICATE: (id: string, certificateId: string) =>
      `/professionals/${id}/certificates/${certificateId}`,
    SPECIALTIES: (id: string) => `/professionals/${id}/specialties`,
    SPECIALTY: (id: string, specialtyId: string) =>
      `/professionals/${id}/specialties/${specialtyId}`,
    SKILLS: (id: string) => `/professionals/${id}/skills`,
    SKILL: (id: string, skillId: string) =>
      `/professionals/${id}/skills/${skillId}`,
    SHIFTS: (id: string) => `/professionals/${id}/shifts`,
    SHIFT: (id: string, shiftId: string) =>
      `/professionals/${id}/shifts/${shiftId}`,
    SHIFT_OPPORTUNITY: (id: string, shiftId: string) =>
      `/professionals/${id}/shifts/opportunity/${shiftId}`,
    APPLIED_SHIFTS: (id: string) => `/professionals/${id}/shifts/applied`,
    UPCOMING_SHIFTS: (id: string) => `/professionals/${id}/shifts/upcoming`,
    APPLY_TO_SHIFT: (id: string, shiftId: string) =>
      `/professionals/${id}/shifts/${shiftId}/apply`,
    QUICK_STATS: (id: string) => `/professionals/${id}/quick-stats`,
    CALENDAR_STATS: (id: string) => `/professionals/${id}/calendar-stats`,
    PAYMENTS_OVERVIEW: (id: string) => `/professionals/${id}/payments/overview`,
    TIER_PROGRESS: (id: string) => `/professionals/${id}/tier-progress`,
    REFERRALS: (id: string) => `/professionals/${id}/referrals`,
    SHIFT_DATES: (id: string, shiftId: string) =>
      `/professionals/${id}/shifts/${shiftId}/dates`,
    CHECK_IN_SHIFT_DATE: (id: string, shiftDateId: string) =>
      `/professionals/${id}/shift-dates/${shiftDateId}/check-in`,
    CHECK_OUT_SHIFT_DATE: (id: string, shiftDateId: string) =>
      `/professionals/${id}/shift-dates/${shiftDateId}/check-out`,
    COMPLETE_SHIFT_DATE: (id: string, shiftDateId: string) =>
      `/professionals/${id}/shift-dates/${shiftDateId}/complete`,
    CONFIRM_PAYMENT: (id: string, paymentId: string) =>
      `/professionals/${id}/payments/${paymentId}/confirm`,
    WITHDRAW_SHIFT: (id: string, shiftId: string) =>
      `/professionals/${id}/shifts/${shiftId}/withdraw`,
    ACCEPT_SHIFT_NOMINATION: (id: string, shiftId: string) =>
      `/professionals/${id}/shifts/${shiftId}/accept-nomination`,
    RATE_PRACTICE: (id: string, practiceId: string) =>
      `/professionals/${id}/practices/${practiceId}/rate`,
    RATINGS: (id: string) => `/professionals/${id}/ratings`,
    NOTIFICATIONS: (id: string) => `/professionals/${id}/notifications`,
    DISPUTES: (id: string) => `/professionals/${id}/disputes`,
    RAISE_DISPUTE: (id: string) => `/professionals/${id}/disputes`,
    SETTINGS: (id: string) => `/professionals/${id}/settings`,
    SEARCH_SHIFTS: (id: string) => `/professionals/${id}/search-shifts`,
    RECOMMENDED_SHIFTS: (id: string) => `/professionals/${id}/recommended-shifts`,
    RECOMMENDATION_EVENTS: (id: string) =>
      `/professionals/${id}/recommendation-events`,
    FAVOURITE_PRACTICES: (id: string) => `/professionals/${id}/favourites/practices`,
    ADD_PRACTICE_TO_FAVOURITES: (id: string, practiceId: string) =>
      `/professionals/${id}/practices/${practiceId}/add-to-favourites`,
    REMOVE_PRACTICE_FROM_FAVOURITES: (id: string, practiceId: string) =>
      `/professionals/${id}/practices/${practiceId}/remove-from-favourites`,
  },
  PRACTICES: {
    STATS: (id: string) => `/practices/${id}/stats`,
    DASHBOARD_MOBILE: (id: string) => `/practices/${id}/dashboard/mobile`,
    TIER_PROGRESS: (id: string) => `/practices/${id}/tier-progress`,
    PRICING_PLAN_SUBSCRIPTION: (id: string) => `/practices/${id}/pricing-plan-subscription`,
    /** Staff verification decision on a practice's credentials. */
    VERIFY: (id: string) => `/practices/${id}/verify`,
    BILLING: (id: string) => `/practices/${id}/billing`,
    BILLING_SETUP_INTENT: (id: string) => `/practices/${id}/billing/setup-intent`,
    BILLING_PAYMENT_METHOD: (id: string) => `/practices/${id}/billing/payment-method`,
    /** Tempii's own shift fees against this practice. */
    FEES: (id: string) => `/practices/${id}/fees`,
    /** Billing history: one row per invoiced plan period. */
    SUBSCRIPTION_INVOICES: (id: string) => `/practices/${id}/subscription-invoices`,
    SHIFT_DATES: (id: string, shiftId: string) =>
      `/practices/${id}/shifts/${shiftId}/dates`,
    APPROVE_SHIFT_DATE: (id: string, shiftDateId: string) =>
      `/practices/${id}/shift-dates/${shiftDateId}/approve`,
    REJECT_SHIFT_DATE: (id: string, shiftDateId: string) =>
      `/practices/${id}/shift-dates/${shiftDateId}/reject`,
    DECLARE_PAYMENT: (id: string, paymentId: string) =>
      `/practices/${id}/payments/${paymentId}/declare`,
    UPLOAD_PAYMENT_EVIDENCE: (id: string, paymentId: string) =>
      `/practices/${id}/payments/${paymentId}/evidence`,
    GET: (id: string) => `/practices/${id}`,
    UPDATE: (id: string) => `/practices/${id}/update-profile`,
    REGISTER: "/practices/register-practice",
    DOCUMENTS: (id: string) => `/practices/${id}/documents`,
    UPLOAD_DOCUMENT: (id: string) => `/practices/${id}/upload-document`,
    DOCUMENT: (id: string, documentId: string) =>
      `/practices/${id}/documents/${documentId}`,
    DOWNLOAD_DOCUMENT: (id: string, documentId: string) =>
      `/practices/${id}/documents/${documentId}/download`,
    BRANCHES: (id: string) => `/practices/${id}/branches`,
    BRANCH: (id: string, branchId: string) =>
      `/practices/${id}/branches/${branchId}`,
    USERS: (id: string) => `/practices/${id}/users`,
    SHIFTS: (id: string) => `/practices/${id}/shifts`,
    OPEN_SHIFT_REQUESTS: (id: string) => `/practices/${id}/shifts/open-requests`,
    SHIFT: (id: string, shiftId: string) => `/practices/${id}/shifts/${shiftId}`,
    RECOMMENDED_SHIFT_PROFESSIONALS: (id: string, shiftId: string) =>
      `/practices/${id}/shifts/${shiftId}/recommended-professionals`,
    RECOMMENDED_PROFESSIONALS_FEED: (id: string) =>
      `/practices/${id}/shifts/recommended-professionals-feed`,
    RECOMMENDATION_EVENTS: (id: string) =>
      `/practices/${id}/recommendation-events`,
    SHIFT_LOOKUPS: (id: string) => `/practices/${id}/shift-lookups`,
    SHIFT_TEMPLATES: (id: string) => `/practices/${id}/shifts/templates`,
    SAVE_SHIFT_TEMPLATE: (id: string) =>
      `/practices/${id}/shifts/templates/save`,
    DELETE_SHIFT_TEMPLATE: (id: string, templateId: string) =>
      `/practices/${id}/shifts/templates/${templateId}`,
    UPLOAD_SHIFT_TEMPLATE_DOCUMENT: (id: string, templateId: string) =>
      `/practices/${id}/shifts/templates/${templateId}/documents`,
    REMOVE_SHIFT_TEMPLATE_DOCUMENT: (
      id: string,
      templateId: string,
      documentId: string
    ) =>
      `/practices/${id}/shifts/templates/${templateId}/documents/${documentId}`,
    UPDATE_SHIFT_POST: (
      id: string,
      shiftId: string,
      shiftPostId: string
    ) => `/practices/${id}/shifts/${shiftId}/posts/${shiftPostId}`,
    UPDATE_SHIFT_STATUS: (id: string, shiftId: string) =>
      `/practices/${id}/shifts/${shiftId}/status`,
    CANCEL_SHIFT: (id: string, shiftId: string) =>
      `/practices/${id}/shifts/${shiftId}/cancel`,
    UPLOAD_SHIFT_DOCUMENT: (id: string, shiftId: string) =>
      `/practices/${id}/shifts/${shiftId}/documents`,
    REMOVE_SHIFT_DOCUMENT: (id: string, shiftId: string, documentId: string) =>
      `/practices/${id}/shifts/${shiftId}/documents/${documentId}`,
    SAVE_DRAFT_SHIFT: (id: string) =>
      `/practices/${id}/shifts/drafts/save`,
    ACCEPT_APPLICATION: (
      id: string,
      shiftId: string,
      applicationId: string
    ) =>
      `/practices/${id}/shifts/${shiftId}/applications/${applicationId}/accept`,
    REJECT_APPLICATION: (
      id: string,
      shiftId: string,
      applicationId: string
    ) =>
      `/practices/${id}/shifts/${shiftId}/applications/${applicationId}/reject`,
    NOMINATE_PROFESSIONAL: (id: string, shiftId: string) =>
      `/practices/${id}/shifts/${shiftId}/applications/nominate`,
    PAYMENTS_OVERVIEW: (id: string) => `/practices/${id}/payments/overview`,
    RATE_PROFESSIONAL: (id: string, professionalId: string) =>
      `/practices/${id}/professionals/${professionalId}/rate`,
    RATINGS: (id: string) => `/practices/${id}/ratings`,
    NOTIFICATIONS: (id: string) => `/practices/${id}/notifications`,
    DISPUTES: (id: string) => `/practices/${id}/disputes`,
    RAISE_DISPUTE: (id: string) => `/practices/${id}/disputes`,
    SETTINGS: (id: string) => `/practices/${id}/settings`,
    SEARCH_PROFESSIONALS: (id: string) => `/practices/${id}/search-professionals`,
    FAVOURITE_PROFESSIONALS: (id: string) => `/practices/${id}/favourites/professionals`,
    ADD_PROFESSIONAL_TO_FAVOURITES: (id: string, professionalId: string) =>
      `/practices/${id}/professionals/${professionalId}/add-to-favourites`,
    REMOVE_PROFESSIONAL_FROM_FAVOURITES: (id: string, professionalId: string) =>
      `/practices/${id}/professionals/${professionalId}/remove-from-favourites`,
  },
  // Shifts are reached through their owning practice or professional; there is no
  // top-level /shifts CRUD on the API, and the constants for one were never called.
  SHIFTS: {
    STATUSES_COUNT: (practiceId: string) =>
      `/practices/${practiceId}/shifts/statuses`,
    APPLICATIONS: "/shifts/applications"
  },
  PAYMENTS: {
    STATS: "/payments/stats",
    /** Proof of payment, readable by either party to the payment. */
    EVIDENCE: (paymentId: string) => `/payments/${paymentId}/evidence`,
    WEBHOOK: "/payments/webhooks/stripe",
  },
  USERS: {
    PROFILE: (id: string) => `/users/get-user-profile/${id}`,
    DOCUMENTS: (id: string) => `/users/${id}/documents`,
    UPLOAD_DOCUMENT: (id: string) => `/users/${id}/documents/upload-document`,
    DOCUMENT: (id: string, documentId: string) =>
      `/users/${id}/documents/${documentId}`,
    MARK_NOTIFICATION_READ: (id: string, notificationId: string) =>
      `/users/${id}/notifications/${notificationId}/read`,
    MARK_ALL_NOTIFICATIONS_READ: (id: string) =>
      `/users/${id}/notifications/read-all`,
    NOTIFICATIONS: (id: string) => `/users/${id}/notifications`,
    DELETE_ACCOUNT: (id: string) => `/users/${id}`,
  },
  ADMIN: {
    STATS: "/admin/stats",
    USERS: "/admin/users",
    USER: (id: string) => `/admin/users/${id}`,
    SUSPEND_USER: (id: string) => `/admin/users/${id}/suspend`,
    REINSTATE_USER: (id: string) => `/admin/users/${id}/reinstate`,
    /** The tenant's professional vocabulary. Retired entries included. */
    CATALOGUE: "/admin/catalogue",
    CATALOGUE_SAVE: (kind: string) => `/admin/catalogue/${kind}`,
    CATALOGUE_SET_ACTIVE: (kind: string, entryId: string) =>
      `/admin/catalogue/${kind}/${entryId}/active`,
    /** What Tempii charges, and who is on what. */
    PRICING_PLANS: "/admin/pricing-plans",
    PRICING_PLAN_LIVE: (planId: string) => `/admin/pricing-plans/${planId}/live`,
    PRICING_PLAN_DEFAULT: (planId: string) => `/admin/pricing-plans/${planId}/default`,
    PRICING_PLAN_HISTORY: (planId: string) => `/admin/pricing-plans/${planId}/history`,
    CURRENCIES: "/admin/currencies",
    SHIFTS: "/admin/shifts",
    TIERS: (kind: string) => `/admin/tiers/${kind}`,
    TIER_ACTIVE: (kind: string, tierId: string) =>
      `/admin/tiers/${kind}/${tierId}/active`,
    TIER_DEFAULT: (kind: string, tierId: string) =>
      `/admin/tiers/${kind}/${tierId}/default`,
    TIER_REWARDS: (kind: string, tierId: string) =>
      `/admin/tiers/${kind}/${tierId}/rewards`,
    TIER_REWARD: (kind: string, tierId: string, rewardId: string) =>
      `/admin/tiers/${kind}/${tierId}/rewards/${rewardId}`,
    PURGE_USER: (id: string) => `/admin/users/${id}/purge`,
    PRACTICES: "/admin/practices",
    PROFESSIONALS: "/admin/professionals",
    PAYMENTS: "/admin/payments",
    DISPUTES: "/admin/disputes",
    FEES: "/admin/fees",
    WAIVE_FEE: (feeId: string) => `/admin/fees/${feeId}/waive`,
    SUBSCRIPTION_INVOICES: "/admin/subscription-invoices",
    WAIVE_SUBSCRIPTION_INVOICE: (invoiceId: string) =>
      `/admin/subscription-invoices/${invoiceId}/waive`,
    RATINGS: "/admin/ratings",
    FLAG_RATING: (ratingId: string) => `/admin/ratings/${ratingId}/flag`,
    DELETE_RATING: (ratingId: string) => `/admin/ratings/${ratingId}`,
    APPLICATIONS: "/admin/applications",
    REVIEW_APPLICATION: (userType: string, applicationId: string) =>
      `/admin/applications/${userType}/${applicationId}/review`,
    RESOLVE_DISPUTE: (paymentId: string) => `/admin/disputes/${paymentId}/resolve`,
    REVIEW_DOCUMENT: (documentId: string) => `/admin/documents/${documentId}/review`,
  }
};
